import { useEffect } from 'react';

import { Alert, Button } from '@/ui';

import { formatAssistantAppointmentTime } from './AssistantAppointmentCard';
import { driveAssistantOperation } from './operationDriver';
import { PAGE_LOAD_ID, removeAssistantOperation } from './operationRecords';
import { useEndAssistantSession } from './useAssistantSession';

import type {
  AssistantBookOperationRecord,
  AssistantOperationRecord,
} from './operationRecords';
import type { FC, ReactNode } from 'react';

import { useAppDispatch } from '@/store/hooks';

const REFUSALS: Record<string, string> = {
  appointment_changed:
    'This session changed before the request reached Mento, so nothing was changed. Check its new details and try again.',
  calendar_check_unavailable:
    'We couldn’t check the calendar just now, so nothing was changed. Try again in a few minutes.',
  forbidden: 'You can’t change this session any more.',
  idempotency_conflict:
    'This request clashed with an earlier one, so nothing was changed. Try again.',
  member_engagement_end_missing:
    'This session can’t be changed online right now. Email concierge@mento.co for help.',
  member_engagement_ended:
    'This person’s coaching has ended, so the session can’t be changed online. Email concierge@mento.co for help.',
  member_engagement_unavailable:
    'We couldn’t check this person’s coaching just now, so nothing was changed. Try again in a few minutes.',
  not_cancellable:
    'This session can’t be cancelled online any more. Email concierge@mento.co for help.',
  not_found: 'We couldn’t find this session any more.',
  not_reschedulable:
    'This session can’t be rescheduled online any more. Email concierge@mento.co for help.',
  slot_unavailable:
    'That time was just taken, so nothing was changed. Choose another time.',
};

const DEFAULT_REFUSAL =
  'Mento couldn’t make this change, so nothing was changed. Email concierge@mento.co if it keeps happening.';

const BOOK_REFUSALS: Record<string, string> = {
  calendar_check_unavailable:
    'We couldn’t check the calendar just now, so nothing was booked. Try again in a few minutes.',
  coach_not_bookable:
    'Sessions with this coach can’t be booked online. Email concierge@mento.co and we’ll help.',
  forbidden: 'You can’t book for this person any more.',
  idempotency_conflict:
    'This request clashed with an earlier one, so nothing was booked. Try again.',
  member_engagement_end_missing:
    'This can’t be booked online right now. Email concierge@mento.co for help.',
  member_engagement_ended:
    'This person’s coaching has ended, so nothing can be booked online. Email concierge@mento.co for help.',
  member_engagement_unavailable:
    'We couldn’t check this person’s coaching just now, so nothing was booked. Try again in a few minutes.',
  not_bookable:
    'There’s nothing to book with this coach right now, so nothing was booked.',
  not_found: 'We couldn’t find this booking any more.',
  slot_unavailable:
    'That time was just taken, so nothing was booked. Choose another time.',
};

const DEFAULT_BOOK_REFUSAL =
  'Mento couldn’t make this booking, so nothing was booked. Email concierge@mento.co if it keeps happening.';

const noun = (record: AssistantOperationRecord) => {
  switch (record.action) {
    case 'book':
      return 'booking';
    case 'reschedule':
      return 'reschedule';
    case 'cancel':
    default:
      return 'cancellation';
  }
};

/** e.g. "Wednesday, November 4 at 10:00 AM EST", in the viewer's zone. */
const formatStart = (startAt: string) => {
  const start = new Date(startAt);
  const date = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'long',
    weekday: 'long',
  }).format(start);
  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(start);
  return `${date} at ${time}`;
};

const bookedMessage = (
  record: AssistantBookOperationRecord,
  startAt: string,
): string => {
  const when = formatStart(startAt);
  const coach = record.coachFirstName;
  switch (record.booking) {
    case 'chemistry':
      return `Chemistry call with ${coach} booked for ${when}. The calendar invite is on its way.`;
    case 'series':
      // No count: Moments sets it when it books, and the operation returns
      // only the first session, so a count here could be stale.
      return `Biweekly sessions with ${coach} booked, starting ${when}. The calendar invites are on their way.`;
    case 'single':
    default:
      return `Single session with ${coach} booked for ${when}. The calendar invite is on its way.`;
  }
};

const refusalMessage = (record: AssistantOperationRecord, error: string) =>
  record.action === 'book'
    ? (BOOK_REFUSALS[error] ?? DEFAULT_BOOK_REFUSAL)
    : (REFUSALS[error] ?? DEFAULT_REFUSAL);

export const assistantOperationMessage = (
  record: AssistantOperationRecord,
): { body: string; severity: 'error' | 'info' | 'success' | 'warning' } => {
  if (record.state === 'submitting') {
    return record.unansweredInPage
      ? {
          body: `We couldn’t confirm that your ${noun(record)} reached Mento. Try again: it won’t be applied twice.`,
          severity: 'warning',
        }
      : { body: `Sending your ${noun(record)}…`, severity: 'info' };
  }
  if (record.state === 'pending' || !record.outcome) {
    return {
      body: `Mento is updating the calendar for your ${noun(record)}…`,
      severity: 'info',
    };
  }
  const { outcome } = record;
  if ('error' in outcome) {
    return { body: refusalMessage(record, outcome.error), severity: 'error' };
  }
  switch (outcome.status) {
    case 'confirmed': {
      if (record.action === 'book') {
        return {
          body: bookedMessage(record, outcome.startAt ?? record.startAt),
          severity: 'success',
        };
      }
      if (record.action === 'cancel') {
        return {
          body: 'Cancelled. The calendar invite is updated.',
          severity: 'success',
        };
      }
      const moved = outcome.startAt
        ? formatAssistantAppointmentTime({
            endAt: outcome.startAt,
            startAt: outcome.startAt,
          })
        : null;
      return {
        body: moved
          ? `Rescheduled to ${moved.date}. The calendar invite is updated.`
          : 'Rescheduled. The calendar invite is updated.',
        severity: 'success',
      };
    }
    case 'needs_reconciliation':
      return {
        body: `Mento accepted the ${noun(record)}, but the calendar needs a check. Our team will follow up; there’s nothing more to do.`,
        severity: 'warning',
      };
    case 'rejected':
    default:
      if (record.action === 'book') {
        return {
          body: 'The booking didn’t go through, so nothing was booked. Choose another time and try again.',
          severity: 'error',
        };
      }
      return {
        body: `The ${noun(record)} didn’t go through, so the session is unchanged. Try again or choose another time.`,
        severity: 'error',
      };
  }
};

/**
 * One book, reschedule or cancel and its state. Mounting it finishes the command:
 * a `submitting` record is sent (or, after a reload, replayed with the same
 * bytes and key), and a `pending` one is polled.
 */
export const AssistantOperationNotice: FC<{
  record: AssistantOperationRecord;
}> = ({ record }) => {
  const dispatch = useAppDispatch();
  const endSession = useEndAssistantSession();
  const { body, severity } = assistantOperationMessage(record);
  const waitingForRetry =
    record.state === 'submitting' && record.unansweredInPage === PAGE_LOAD_ID;

  useEffect(() => {
    // A send that got no answer in this page waits for "Try again"; one from
    // an earlier page load (a reload) is replayed straight away.
    if (waitingForRetry) return;
    driveAssistantOperation(dispatch, record, () => endSession('expired'));
  }, [dispatch, endSession, record, waitingForRetry]);

  const retry = () =>
    driveAssistantOperation(dispatch, record, () => endSession('expired'));
  let action: ReactNode;
  if (record.state === 'settled') {
    action = (
      <Button
        color="inherit"
        size="small"
        onClick={() => removeAssistantOperation(record)}
      >
        Done
      </Button>
    );
  } else if (waitingForRetry) {
    action = (
      <Button color="inherit" size="small" onClick={retry}>
        Try again
      </Button>
    );
  }

  return (
    <Alert
      action={action}
      role={severity === 'error' ? 'alert' : 'status'}
      severity={severity}
    >
      <strong>{record.summary}</strong>
      <br />
      {body}
    </Alert>
  );
};
